/**
 * Google Meet Integration Utility Functions for Clinician Web
 */

export const copyMeetingLink = async (meetingLink: string) => {
  try {
    await navigator.clipboard.writeText(meetingLink);
    return { success: true, message: 'Meeting link copied to clipboard!' };
  } catch (error) {
    const textArea = document.createElement('textarea');
    textArea.value = meetingLink;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return { success: true, message: 'Meeting link copied!' };
    } catch (err) {
      document.body.removeChild(textArea);
      return { success: false, message: 'Failed to copy link' };
    }
  }
};

export const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export const formatDateTime = (dateTimeString: string) => {
  if (!dateTimeString) return 'N/A';

  return new Date(dateTimeString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatSessionStatus = (status: string) => {
  const statusMap: { [key: string]: string } = {
    'PENDING': 'Pending Payment',
    'PAID': 'Confirmed',
    'STARTED': 'In Progress',
    'COMPLETED': 'Completed',
    'REPORT_SUBMITTED': 'Report Submitted',
    'CANCELLED': 'Cancelled',
    'REJECTED': 'Rejected'
  };

  return statusMap[status] || status;
};

export const getStatusBadgeColor = (status: string, themeColors?: any) => {
  if (!themeColors) {
    const fallbackColorMap: { [key: string]: { bg: string; color: string } } = {
      'PENDING': { bg: '#fff3cd', color: '#856404' },
      'PAID': { bg: '#d4edda', color: '#155724' },
      'STARTED': { bg: '#cce5ff', color: '#004085' },
      'COMPLETED': { bg: '#d1ecf1', color: '#0c5460' },
      'CANCELLED': { bg: '#f8d7da', color: '#721c24' },
      'REJECTED': { bg: '#f8d7da', color: '#721c24' }
    };
    return fallbackColorMap[status] || { bg: '#e9ecef', color: '#495057' };
  }

  const colorMap: { [key: string]: { bg: string; color: string } } = {
    'PENDING': {
      bg: themeColors.warning.lighter,
      color: themeColors.warning.dark
    },
    'PAID': {
      bg: themeColors.success.lighter,
      color: themeColors.success.dark
    },
    'STARTED': {
      bg: themeColors.info.lighter,
      color: themeColors.info.dark
    },
    'COMPLETED': {
      bg: themeColors.primary.lighter,
      color: themeColors.primary.dark
    },
    'CANCELLED': {
      bg: themeColors.error.lighter,
      color: themeColors.error.dark
    },
    'REJECTED': {
      bg: themeColors.error.lighter,
      color: themeColors.error.dark
    }
  };

  return colorMap[status] || {
    bg: themeColors.alpha.black[5],
    color: themeColors.alpha.black[70]
  };
};

export const createStatusBadgeColorGetter = (themeColors: any) => {
  return (status: string) => getStatusBadgeColor(status, themeColors);
};

export const canShowMeetLink = (session: any) => {
  return session?.meetingLink && ['PAID', 'STARTED', 'COMPLETED'].includes(session?.status);
};

export const isVideoSession = (session: any) => {
  return session?.consultationMode?.toLowerCase() === 'video';
};

export const getMeetingStartTime = (session: any) => {
  if (!session?.date) return null;

  if (session?.sessionTimeslots?.length > 0) {
    const firstSlot = session.sessionTimeslots[0];
    if (firstSlot?.timeslot?.startAt) {
      return `${session.date}T${firstSlot.timeslot.startAt}:00.000Z`;
    }
  }

  return `${session.date}T09:00:00.000Z`;
};

export const getMeetingEndTime = (session: any) => {
  if (!session?.date) return null;

  if (session?.sessionTimeslots?.length > 0) {
    const lastSlot = session.sessionTimeslots[session.sessionTimeslots.length - 1];
    if (lastSlot?.timeslot?.endAt) {
      return `${session.date}T${lastSlot.timeslot.endAt}:00.000Z`;
    }
  }

  return `${session.date}T10:00:00.000Z`;
};

export const createGoogleCalendarUrl = (session: any) => {
  if (!session) return null;

  const startTime = getMeetingStartTime(session);
  const endTime = getMeetingEndTime(session);

  if (!startTime || !endTime) return null;

  try {
    const title = encodeURIComponent(`Therapy Session - ${session.patientProfile?.fullName || 'Patient'}`);
    const description = encodeURIComponent(
      `Video Consultation Session\n\n` +
      `Clinician: ${session.clinician?.user?.fullName || 'N/A'}\n` +
      `Patient: ${session.patientProfile?.fullName || 'N/A'}\n` +
      `Mode: ${session.consultationMode || 'Video'}\n` +
      `Session ID: ${session.id || session.sessionId || 'N/A'}\n\n` +
      `Meeting Link: ${session.meetingLink || 'Will be provided before the session'}\n\n` +
      `Please join the meeting at the scheduled time.`
    );

    const startFormatted = new Date(startTime).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const endFormatted = new Date(endTime).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

    const calendarUrl = `https://calendar.google.com/calendar/u/0/r/eventedit?text=${title}&dates=${startFormatted}/${endFormatted}&details=${description}`;

    return calendarUrl;
  } catch (error) {
    console.error('Error creating calendar URL:', error);
    return null;
  }
};

export const openMeetingLink = (meetingLink: string) => {
  if (meetingLink) {
    window.open(meetingLink, '_blank');
  }
};

export const canJoinMeeting = (session: any) => {
  if (!session?.date) return false;

  const startTime = getMeetingStartTime(session);
  if (!startTime) return false;

  const now = new Date();
  const meetingStart = new Date(startTime);
  const timeDiff = meetingStart.getTime() - now.getTime();
  const minutesDiff = timeDiff / (1000 * 60);

  return minutesDiff <= 15 && minutesDiff >= -120;
};
